import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Building2, Save, Mail, MessageCircle, ShieldCheck, Send, QrCode, DatabaseBackup, Upload, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function FirmSettings() {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const canEdit = user?.role === "Owner";

  useEffect(() => { api.get("/firm").then(({ data }) => setForm(data)); }, []);

  async function save(e) {
    e.preventDefault();
    if (!canEdit) { toast.error("Only Owner can update firm settings"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      // don't send back the placeholder we render
      if (!payload.smtp_password) delete payload.smtp_password;
      delete payload.smtp_password_configured;
      const { data } = await api.put("/firm", payload);
      setForm({ ...data, smtp_password: "" });
      toast.success("Firm settings updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  }

  async function testSmtp() {
    setTesting(true);
    try {
      const { data } = await api.post("/notifications/test-smtp");
      toast.success(`Test email sent to ${data.sent_to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  }

  if (!form) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="firm-settings-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
          <Building2 className="w-3 h-3" /> Business identity
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Firm Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">This info appears on every invoice PDF, shareable payment link, and client notification.</p>
      </div>

      <form onSubmit={save} className="space-y-5">
        {/* Firm identity */}
        <Card className="p-6 space-y-4">
          <h3 className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-widest">Identity</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Firm Name</Label><Input data-testid="firm-name-input" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Tagline</Label><Input value={form.tagline || ""} onChange={e => setForm({ ...form, tagline: e.target.value })} /></div>
            <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address || ""} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>PAN</Label><Input value={form.pan || ""} onChange={e => setForm({ ...form, pan: e.target.value.toUpperCase() })} className="font-mono uppercase" /></div>
            <div><Label>GSTIN</Label><Input value={form.gstin || ""} onChange={e => setForm({ ...form, gstin: e.target.value.toUpperCase() })} className="font-mono uppercase" /></div>
            <div className="col-span-2"><Label>Bank Details (on invoice)</Label><Textarea rows={2} value={form.bank_details || ""} onChange={e => setForm({ ...form, bank_details: e.target.value })} /></div>
          </div>
        </Card>

        {/* WhatsApp */}
        <Card className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-widest flex items-center gap-2">
              <QrCode className="w-4 h-4 text-blue-600" /> UPI Payment (auto QR on every invoice)
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>UPI ID (VPA)</Label>
              <Input data-testid="firm-upi-id-input" placeholder="9748365000@ybl" value={form.upi_id || ""} onChange={e => setForm({ ...form, upi_id: e.target.value })} className="font-mono" />
              <div className="text-[10px] text-muted-foreground mt-1">Example: <span className="font-mono">9748365000@ybl</span>, <span className="font-mono">vijay@icici</span>, <span className="font-mono">vsvyas@paytm</span></div>
            </div>
            <div><Label>Display name (shown on payer's app)</Label>
              <Input placeholder="VIJAY SHANKAR VYAS" value={form.upi_name || ""} onChange={e => setForm({ ...form, upi_name: e.target.value })} />
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 p-3 rounded-md leading-relaxed">
            When set, every invoice PDF and payment page will include a <b>dynamic UPI QR</b> that pre-fills the balance amount + invoice number. Clients scan with GPay/PhonePe/Paytm/BHIM and pay instantly — no manual amount entry needed.
          </div>
        </Card>

        {/* WhatsApp */}
        <Card className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-widest flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-emerald-600" /> WhatsApp
            </h3>
            <Badge variant="outline" className="text-[10px]">wa.me deep-link</Badge>
          </div>
          <div><Label>Your WhatsApp Number (with country code)</Label>
            <Input data-testid="firm-whatsapp-input" placeholder="+91-9876543210" value={form.whatsapp_number || ""} onChange={e => setForm({ ...form, whatsapp_number: e.target.value })} className="font-mono" />
            <div className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">
              Notifications open <span className="font-mono">wa.me/&lt;client&gt;</span> in <b>your</b> WhatsApp with the message pre-filled — you tap Send. This works from your logged-in WhatsApp on any device.
              For fully-automated sending (no manual tap), a Twilio WhatsApp Business setup is required — we can add that later.
            </div>
          </div>
        </Card>

        {/* SMTP / Email */}
        <Card className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-widest flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-600" /> Email delivery (SMTP)
            </h3>
            <div className="flex items-center gap-2">
              {form.smtp_password_configured && (
                <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]"><ShieldCheck className="w-3 h-3 mr-1" />Password set</Badge>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>SMTP Host</Label><Input placeholder="smtp.gmail.com" value={form.smtp_host || ""} onChange={e => setForm({ ...form, smtp_host: e.target.value })} className="font-mono" /></div>
            <div><Label>Port</Label><Input type="number" placeholder="587" value={form.smtp_port || 587} onChange={e => setForm({ ...form, smtp_port: parseInt(e.target.value || 587) })} /></div>
            <div><Label>Username</Label><Input placeholder="you@gmail.com" value={form.smtp_user || ""} onChange={e => setForm({ ...form, smtp_user: e.target.value })} className="font-mono" /></div>
            <div><Label>Password / App Password</Label>
              <Input data-testid="smtp-password-input" type="password" placeholder={form.smtp_password_configured ? "•••••••• (leave blank to keep)" : "app password"} value={form.smtp_password || ""} onChange={e => setForm({ ...form, smtp_password: e.target.value })} />
            </div>
            <div><Label>From Address</Label><Input placeholder="you@gmail.com" value={form.smtp_from || ""} onChange={e => setForm({ ...form, smtp_from: e.target.value })} className="font-mono" /></div>
            <div><Label>From Name</Label><Input placeholder="Vijay Shankar Vyas" value={form.smtp_from_name || ""} onChange={e => setForm({ ...form, smtp_from_name: e.target.value })} /></div>
          </div>
          <div className="text-[11px] text-muted-foreground bg-slate-50 dark:bg-zinc-900 p-3 rounded-md border border-border leading-relaxed">
            <b>Gmail:</b> host <span className="font-mono">smtp.gmail.com</span>, port <span className="font-mono">587</span>. Use an <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-blue-600 underline">App Password</a> (not your Google login password) after enabling 2-Step Verification.
            <br /><b>Outlook / Office 365:</b> host <span className="font-mono">smtp.office365.com</span>, port <span className="font-mono">587</span>.
            <br /><b>Zoho Mail:</b> host <span className="font-mono">smtp.zoho.in</span>, port <span className="font-mono">587</span>.
          </div>
          {form.smtp_password_configured && canEdit && (
            <Button type="button" data-testid="test-smtp-btn" variant="outline" onClick={testSmtp} disabled={testing}>
              <Send className="w-4 h-4 mr-1.5" /> {testing ? "Sending test…" : "Send test email to me"}
            </Button>
          )}
        </Card>

        {/* Signature */}
        <Card className="p-6 space-y-4">
          <h3 className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-widest">Notification signature</h3>
          <div>
            <Label>Signature (used as <span className="font-mono text-[10px] bg-slate-100 dark:bg-zinc-800 px-1 rounded">{"{signature}"}</span> inside your template)</Label>
            <Textarea data-testid="firm-signature-input" rows={2} value={form.signature || ""} onChange={e => setForm({ ...form, signature: e.target.value })} />
            <div className="text-[10px] text-muted-foreground mt-1">Example: "Team Vijay" or "Regards, Vijay Shankar Vyas"</div>
          </div>
        </Card>

        {/* Message Template */}
        <Card className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-widest flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-blue-600" /> Notification Message Template
            </h3>
            <Badge variant="outline" className="text-[10px]">used for WhatsApp &amp; Email</Badge>
          </div>
          <div>
            <Label>Message body</Label>
            <Textarea
              data-testid="firm-template-input"
              rows={9}
              value={form.notification_template || ""}
              onChange={e => setForm({ ...form, notification_template: e.target.value })}
              className="font-mono text-xs"
            />
            <div className="text-[11px] text-muted-foreground mt-2 leading-relaxed space-y-1">
              <div>Available placeholders — they are replaced per client / per task:</div>
              <div className="grid grid-cols-2 gap-1 mt-1.5 max-w-md">
                <div className="font-mono text-[10px]"><span className="bg-slate-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded">{"{client_name}"}</span> — client's name</div>
                <div className="font-mono text-[10px]"><span className="bg-slate-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded">{"{task_title}"}</span> — completed task</div>
                <div className="font-mono text-[10px]"><span className="bg-slate-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded">{"{signature}"}</span> — from above</div>
                <div className="font-mono text-[10px]"><span className="bg-slate-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded">{"{firm_name}"}</span> — your firm name</div>
              </div>
            </div>
          </div>

          {/* Live preview */}
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Preview (with sample data)</Label>
            <div className="mt-1.5 p-4 rounded-md border border-border bg-slate-50 dark:bg-zinc-900 text-xs whitespace-pre-wrap font-mono">
              {(form.notification_template || "")
                .replaceAll("{client_name}", "Sharma Textiles Pvt Ltd")
                .replaceAll("{task_title}", "GSTR-3B for July 2026")
                .replaceAll("{signature}", form.signature || "Team Vijay")
                .replaceAll("{firm_name}", form.name || "Vijay Shankar Vyas") ||
                <span className="text-muted-foreground italic">Type a template above to see preview.</span>}
            </div>
          </div>
        </Card>

        <BackupRestoreCard canEdit={canEdit} />

        <div className="flex items-center justify-end gap-3">
          {!canEdit && <div className="text-[11px] text-muted-foreground italic">Only Owner can edit these fields.</div>}
          <Button data-testid="save-firm-btn" type="submit" disabled={saving || !canEdit} className="bg-blue-600 hover:bg-blue-700">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save all settings"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function BackupRestoreCard({ canEdit }) {
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState("merge");
  const [result, setResult] = useState(null);

  async function downloadBackup() {
    try {
      setDownloading(true);
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "");
      a.download = `tpms_backup_${ts}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backup failed — Owner login required");
    } finally { setDownloading(false); }
  }

  async function uploadRestore(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!confirm(`Restore data from "${file.name}"?\n\nMode: ${mode.toUpperCase()}\n` +
        (mode === "replace"
          ? "⚠ REPLACE will WIPE existing rows in each collection before importing.\n"
          : "MERGE will skip any rows whose _id already exists — safe for topping up.\n") +
        "\nContinue?")) return;
    try {
      setUploading(true);
      setResult(null);
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const { data } = await api.post("/admin/restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Restore complete · mode: ${data.mode}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed");
    } finally { setUploading(false); }
  }

  return (
    <Card className="p-6 space-y-4" data-testid="backup-restore-card">
      <div className="flex items-center gap-2">
        <DatabaseBackup className="w-5 h-5 text-blue-600" />
        <h2 className="text-lg font-semibold">Backup &amp; Restore</h2>
        <Badge variant="outline" className="text-[10px] ml-auto">Owner only</Badge>
      </div>
      <p className="text-sm text-muted-foreground">
        Download a full JSON snapshot of your firm data — clients, credentials (encrypted), tasks,
        invoices, recurring templates, firm settings and counters. Use this to migrate from
        preview → production, or as a scheduled offline backup. Users, audit logs, and
        notifications are excluded by design.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-border rounded-lg p-4 space-y-3 bg-slate-50/50 dark:bg-zinc-900/40">
          <div className="flex items-center gap-2">
            <Download className="w-4 h-4 text-emerald-600" />
            <div className="font-medium text-sm">Export backup</div>
          </div>
          <p className="text-xs text-muted-foreground">
            Downloads one JSON file with everything. Save it somewhere safe. Log in on the target
            environment and upload it below.
          </p>
          <Button
            data-testid="backup-download-btn"
            type="button"
            onClick={downloadBackup}
            disabled={downloading || !canEdit}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Download className="w-4 h-4 mr-1.5" />
            {downloading ? "Preparing…" : "Download backup JSON"}
          </Button>
        </div>

        <div className="border border-border rounded-lg p-4 space-y-3 bg-slate-50/50 dark:bg-zinc-900/40">
          <div className="flex items-center gap-2">
            <Upload className="w-4 h-4 text-blue-600" />
            <div className="font-medium text-sm">Restore from backup</div>
          </div>
          <p className="text-xs text-muted-foreground">
            Pick a mode, then choose the JSON file downloaded from the source environment.
          </p>
          <div className="flex items-center gap-3 text-xs">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="mode" value="merge" checked={mode === "merge"} onChange={() => setMode("merge")} data-testid="restore-mode-merge" />
              <span><b>Merge</b> — add missing, keep existing (safe)</span>
            </label>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="mode" value="replace" checked={mode === "replace"} onChange={() => setMode("replace")} data-testid="restore-mode-replace" />
              <span className="text-red-600"><b>Replace</b> — wipe collections then import (destructive)</span>
            </label>
          </div>
          <div>
            <input
              id="restore-file-input"
              data-testid="restore-file-input"
              type="file"
              accept=".json,application/json"
              onChange={uploadRestore}
              disabled={uploading || !canEdit}
              className="hidden"
            />
            <Button
              type="button"
              disabled={uploading || !canEdit}
              onClick={() => document.getElementById("restore-file-input")?.click()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="restore-upload-btn"
            >
              <Upload className="w-4 h-4 mr-1.5" />
              {uploading ? "Restoring…" : "Choose backup JSON"}
            </Button>
          </div>
        </div>
      </div>

      {result && (
        <div className="border border-emerald-300 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-950/30 rounded p-3 text-xs space-y-1" data-testid="restore-result">
          <div className="font-medium text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5">
            ✓ Restore finished — mode: <b>{result.mode}</b>
          </div>
          {result.source && <div className="text-muted-foreground">Source: {result.source} · Generated {result.generated_at?.slice(0, 19).replace("T", " ")}</div>}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 mt-2">
            {Object.entries(result.summary || {}).map(([name, s]) => (
              <div key={name} className="bg-white dark:bg-zinc-900 rounded px-2 py-1.5 border border-border">
                <div className="font-mono text-[10px] text-muted-foreground">{name}</div>
                <div className="text-[11px]">
                  <span className="text-emerald-600 font-semibold">+{s.inserted}</span>{" inserted · "}
                  <span className="text-blue-600 font-semibold">{s.updated}</span>{" updated · "}
                  <span className="text-slate-500">{s.skipped}</span>{" skipped"}
                  {s.errors > 0 && <span className="text-red-600 font-semibold"> · {s.errors} err</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-start gap-2 text-[11px] text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded p-2.5">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <div>
          Encrypted credentials (Vault) remain encrypted — the encryption key
          (<code>VAULT_KEY</code>) must be identical on both source and target environments for
          them to decrypt after restore. If your production has a different key, re-enter each
          credential after migration.
        </div>
      </div>
    </Card>
  );
}
